import { ThunkAction, ThunkDispatch } from 'redux-thunk';

export interface IMyTodoUserRef {
  _id?: string;
  firstName?: string;
  lastName?: string;
}

// MyTodo interface matching backend schema
export interface IMyTodo {
  _id: string;
  assignedTo?: IMyTodoUserRef;
  assignedBy?: IMyTodoUserRef;
  assignedByName?: string;
  status: 'Pending' | 'Done';
  assignedAt: string;
  location?: string;
  moduleRefId?: string;
  devOrderId?: string | { _id?: string; image?: string; number?: string };
  productRef?: string;
  description?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IMyTodoListRow {
  _id: string;
  groupKey: string;
  devOrderId?: { _id: string; image?: string; number?: string } | null;
  totalTasks: number;
  doneTasks: number;
  completionPercent: number;
  hasExpandData: boolean;
  expandData: IMyTodo[];
  task?: IMyTodo;
}

export const MY_TODO_FILTER_STORAGE_KEY = 'my_todo_filter_persisted';

// Action type constants
export const MyTodoActionTypes = {
  FETCH_MY_TODOS: 'FETCH_MY_TODOS',
  ADD_MY_TODO: 'ADD_MY_TODO',
  UPDATE_MY_TODO_STATUS: 'UPDATE_MY_TODO_STATUS',
  DELETE_MY_TODO: 'DELETE_MY_TODO',
  FETCH_MY_TODO_COUNT: 'FETCH_MY_TODO_COUNT',
  SET_FILTER_VALUES: 'SET_FILTER_VALUES',
  CLEAR_MY_TODO_REDUCER: 'CLEAR_MY_TODO_REDUCER',
  SET_LOADING: 'SET_LOADING',
} as const;

// Action interfaces
export interface FetchMyTodosAction {
  type: typeof MyTodoActionTypes.FETCH_MY_TODOS;
  payload: {
    items: IMyTodoListRow[];
    totalCount: number;
    isScrolled?: boolean;
    requestId?: string;
  };
}

export interface AddMyTodoAction {
  type: typeof MyTodoActionTypes.ADD_MY_TODO;
  payload: IMyTodo;
}

export interface UpdateMyTodoStatusAction {
  type: typeof MyTodoActionTypes.UPDATE_MY_TODO_STATUS;
  payload: {
    id: string;
    status: 'Pending' | 'Done';
  };
}

export interface DeleteMyTodoAction {
  type: typeof MyTodoActionTypes.DELETE_MY_TODO;
  payload: { id?: string; ids?: string[] };
}

export interface FetchMyTodoCountAction {
  type: typeof MyTodoActionTypes.FETCH_MY_TODO_COUNT;
  payload: { pendingCount: number; allTodoPendingCount: number };
}

export interface SetFilterValuesAction {
  type: typeof MyTodoActionTypes.SET_FILTER_VALUES;
  payload: Partial<MyTodoFilterValues>;
}

export interface ClearMyTodoReducerAction {
  type: typeof MyTodoActionTypes.CLEAR_MY_TODO_REDUCER;
}

export interface SetLoadingAction {
  type: typeof MyTodoActionTypes.SET_LOADING;
  payload: boolean;
}

// Union type for all actions
export type MyTodoAction =
  | FetchMyTodosAction
  | AddMyTodoAction
  | UpdateMyTodoStatusAction
  | DeleteMyTodoAction
  | FetchMyTodoCountAction
  | SetFilterValuesAction
  | ClearMyTodoReducerAction
  | SetLoadingAction;

// Filter values interface
export interface MyTodoFilterValues {
  view: 'my-todo' | 'all-todo';
  pageIndex: number;
  pageSize: number;
  assigneeId?: string;
  searchText: string;
  sortOrder: string;
  sortPropertyName: string;
  isScrolled?: boolean;
  statuses?: string[];
  brandId?: string[];
  subBrandId?: string[];
  productType?: string[];
  catOneId?: string[];
  catTwoId?: string[];
  catThreeId?: string[];
  catFourId?: string[];
  supplierId?: string[];
  seasonId?: string[];
  year?: string[];
  month?: string[];
  createdBy?: string[];
  productStatus?: string[];
  licensorId?: string[];
  licenseId?: string[];
  likenessId?: string[];
  colorwayId?: string[];
  colorGroupId?: string[];
  dynamicTypes?: Array<{ colId: string; valId: string }>;
  textCustomAttribute?: string[];
}

// State interface
export interface MyTodoState {
  items: IMyTodoListRow[];
  loading: boolean;
  error: string | undefined;
  pendingCount: number;
  allTodoPendingCount: number;
  totalCount: number;
  filterValues: MyTodoFilterValues;
  currentRequestId: string | null;
}

// Redux state type (you may need to adjust this based on your root state)
export interface RootState {
  MyTodoDetails: MyTodoState;
  UserDetails: {
    userInfo: {
      userId: string;
      role?: string;
    } | null;
    isSupplierUserLoggedIn?: boolean;
  };
  // Add other reducer states as needed
}

// Thunk types
export type MyTodoThunkAction<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, MyTodoAction>;

export type MyTodoThunkDispatch = ThunkDispatch<RootState, unknown, MyTodoAction>;
